import { useUserData } from '../hooks/useUserData';
import { ProxyApi } from '../api/rndApi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpsellSuccessModal } from '../components/UpsellSuccessModal';
import { SHOP_UPSELLS } from '../constants/shop';
import { Modal } from 'react-bootstrap';
import SafeCheckoutImage from '../assets/images/safe_checkout.png';
import PackageIcon from '../assets/icons/package.svg';
import BookFillIcon from '../assets/icons/book-fill.svg';
import CoverIcon from '../assets/icons/cover.svg';
import CustomCover from './CustomCover';
import WoodBackgroundImage from '../assets/images/wood-background.png';

// A simple keyframe for the skeleton animation.
// In a real app, this would typically live in a global CSS file.
const keyframes = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

// --- NEW: Reusable Skeleton Loader Component ---
// This component displays a placeholder with a pulsing animation.
const Skeleton = ({ width, height, style, className }) => {
  const skeletonStyle = {
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    width,
    height,
    ...style,
  };

  return (
    <>
      <style>{keyframes}</style>
      <div style={skeletonStyle} className={className} />
    </>
  );
};

export function UpsellModal({ visible, onHide }) {
  const { t } = useTranslation();
  const { userData } = useUserData();

  const [upsellsLoading, setUpsellsLoading] = useState(true);
  const [upsells, setUpsells] = useState(null);
  const [upsellUrls, setUpsellUrls] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedUpsellIndex, setSelectedUpsellIndex] = useState(0);

  useEffect(() => {
    if (!userData || !userData?.code || !upsellsLoading) return;

    (async () => {
      try {
        const response = await ProxyApi.fetchUpsells(userData?.code);
        const data = response?.data.filter(
          (u) =>
            u.key.includes('product-digital-book-credits-v1') &&
            u.key !== 'product-digital-book-credits-v1-plus2x'
        );
        data.sort((a, b) => a.prices[0].final_price - b.prices[0].final_price);
        setUpsells(data);
        setUpsellUrls(response?.upsell_urls);
      } catch (error) {
        console.error(error);
      } finally {
        setUpsellsLoading(false);
      }
    })();
  }, [userData, upsellsLoading]);

  function handleOrderClick() {
    setShowSuccessModal(true);
  }

  function completeOrder() {
    onHide();
  }

  const selectedUpsell = upsells?.[selectedUpsellIndex];
  const hardcodedData = selectedUpsell
    ? SHOP_UPSELLS[selectedUpsell.key]
    : null;

  const image =
    selectedUpsell?.properties?.find((p) => p.name === 'image')?.value ||
    hardcodedData?.image;

  const initialPrice = selectedUpsell?.prices[0]?.price;
  const finalPrice = selectedUpsell?.prices[0]?.final_price;
  const discountAmount = initialPrice - finalPrice;

  return (
    <Modal
      show={visible}
      onHide={onHide}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="upsell-modal"
    >
      <Modal.Header
        closeButton
        style={{
          backgroundColor: '#F8F9FA',
          borderBottom: '1px solid #E9ECEF',
          paddingBottom: 12,
        }}
      >
        <Modal.Title
          style={{
            fontSize: '20px',
            fontWeight: 600,
            textTransform: 'capitalize',
          }}
        >
          {t('shopScreen.additionalBooks')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="upsell-modal-body"
        style={{ padding: '32px 40px' }}
      >
        <style>{`
          .upsell-modal-content {
            display: flex;
            gap: 48px;
          }
          .upsell-modal-preview {
            flex: 0 0 45%;
          }
          @media (max-width: 992px) {
            .upsell-modal-body {
              padding: 24px 20px !important;
            }
            .upsell-modal-content {
              flex-direction: column;
              gap: 32px;
            }
            .upsell-modal-preview {
              flex: 1;
              min-height: 350px;
            }
          }
        `}</style>
        <div className="upsell-modal-content">
          <div
            className="upsell-modal-preview"
            style={{
              backgroundImage: `url(${WoodBackgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: '8px',
              minHeight: '450px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {userData?.cover && (
              <div style={{ position: 'relative' }}>
                <CustomCover
                  cover={userData?.cover}
                  width={320}
                  coverPhoto={userData?.cover_photo}
                  withPhoto={userData?.cover < 7}
                />
                <div style={{ position: 'absolute', top: -15, left: 25 }}>
                  <CustomCover
                    cover={userData?.cover}
                    width={320}
                    coverPhoto={userData?.cover_photo}
                    withPhoto={userData?.cover < 7}
                    name={userData?.name}
                  />
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <h2
              style={{
                fontSize: '28px',
                fontWeight: 600,
                marginBottom: '16px',
                lineHeight: 1.2,
              }}
            >
              {t('shopScreen.additionalBooksTitle')}
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: '#666',
                marginBottom: '24px',
                lineHeight: 1.5,
              }}
            >
              {t('shopScreen.additionalBooksDescription')}
            </p>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '32px',
                height: '28px',
              }}
            >
              {upsellsLoading ? (
                <Skeleton width="280px" height="28px" />
              ) : (
                <>
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#444',
                      textDecoration: 'line-through',
                    }}
                  >
                    ${initialPrice}
                  </span>
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#d32f2f',
                    }}
                  >
                    {t('shopScreen.additionalBooksCta', {
                      price: finalPrice,
                    })}
                  </span>
                  <span
                    style={{
                      backgroundColor: '#4caf50',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {t('shopScreen.additionalBooksSave', {
                      discount: Math.round(discountAmount),
                    })}
                  </span>
                </>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Select Quantity
              </label>
              {/* --- REFACTORED: Dropdown Section with Skeleton Loader --- */}
              {upsellsLoading ? (
                <Skeleton width="100%" height="54px" />
              ) : (
                <select
                  value={selectedUpsellIndex}
                  onChange={(e) =>
                    setSelectedUpsellIndex(Number(e.target.value))
                  }
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23333' d='M1.41 0L6 4.59L10.59 0L12 1.41l-6 6l-6-6z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center',
                  }}
                >
                  {upsells.map((upsell, index) => {
                    const upsellHardcodedData = SHOP_UPSELLS[upsell.key];
                    const upsellQuantity =
                      upsell?.properties?.find((p) => p.name === 'quantity')
                        ?.value || upsellHardcodedData?.quantity;
                    return (
                      <option key={upsell.id} value={index}>
                        {upsellQuantity && `${upsellQuantity}x `}
                        {t(upsellHardcodedData?.titleKey)}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* --- REFACTORED: Button Section with Skeleton Loader --- */}
            {upsellsLoading ? (
              <Skeleton
                width="100%"
                height="58px"
                style={{ marginBottom: '32px' }}
              />
            ) : (
              <button
                onClick={handleOrderClick}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginBottom: '32px',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => (e.target.style.backgroundColor = '#333')}
                onMouseOut={(e) => (e.target.style.backgroundColor = '#000')}
              >
                Order now - ${finalPrice}
              </button>
            )}

            <div
              style={{
                display: 'flex',
                gap: '24px',
                paddingTop: '24px',
                borderTop: '1px solid #e0e0e0',
                marginBottom: '24px',
              }}
            >
              {[
                { icon: PackageIcon, label: 'FreeShipping' },
                { icon: BookFillIcon, label: 'PremiumQuality' },
                { icon: CoverIcon, label: 'Hardcover' },
              ].map((feature, index) => (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '16px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <img
                      src={feature.icon}
                      alt=""
                      style={{ width: '32px', height: '32px' }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    {t(`shopScreen.additionalBooks${feature.label}`)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '12px',
                  color: '#999',
                  marginBottom: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                Guaranteed Safe Checkout
              </div>
              <img
                src={SafeCheckoutImage}
                alt="safe checkout"
                style={{
                  maxWidth: '100%',
                  height: '32px',
                  objectFit: 'contain',
                }}
              />
            </div>
          </div>
        </div>
      </Modal.Body>

      {selectedUpsell && (
        <UpsellSuccessModal
          onHide={() => setShowSuccessModal(false)}
          onComplete={completeOrder}
          upsellPurchaseUrl={upsellUrls[selectedUpsell.key]}
          upsellImage={image}
          visible={showSuccessModal}
        />
      )}
    </Modal>
  );
}
